<script setup lang="ts">
import { computed } from 'vue'
import type { TimelineProps } from '../../types/experience'
import { revealDirective } from '../../composables/useReveal'

const vReveal = revealDirective

const props = defineProps<TimelineProps>()

/* Newest first, grouped by start year. */
const grouped = computed(() => {
  const map: Record<string, typeof props.experiences> = {}
  props.experiences.forEach((exp) => {
    const year = (exp.startDate || '').slice(0, 4) || 'Earlier'
    map[year] = map[year] || []
    map[year].push(exp)
  })
  return Object.entries(map)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([year, items]) => ({ year, items }))
})

const typeLabels: Record<string, string> = {
  work: 'Role',
  education: 'Education',
  project: 'Project',
  achievement: 'Milestone',
}
</script>

<template>
  <div class="log">
    <section v-for="group in grouped" :key="group.year" class="year" v-reveal>
      <h3 class="year-mark display display-3">{{ group.year }}</h3>

      <div class="entries">
        <article v-for="experience in group.items" :key="experience.id" class="entry">
          <header class="entry-head">
            <div>
              <h4 class="entry-title">
                {{ experience.title }}
                <span v-if="experience.isOngoing" class="ongoing" title="Ongoing">
                  <span class="dot" aria-hidden="true"></span>
                  <span class="sr-only">Ongoing</span>
                </span>
              </h4>
              <p class="entry-org meta">
                {{ experience.company }} · {{ experience.location }}
              </p>
            </div>
            <div class="entry-when">
              <span class="eyebrow">{{ typeLabels[experience.type] ?? experience.type }}</span>
              <time class="meta">{{ experience.duration }}</time>
            </div>
          </header>

          <ul class="bullets">
            <li v-for="item in experience.description" :key="item">{{ item }}</li>
          </ul>

          <ul v-if="experience.technologies.length" class="techs">
            <li v-for="tech in experience.technologies" :key="tech" class="meta">
              {{ tech }}
            </li>
          </ul>
        </article>
      </div>
    </section>
  </div>
</template>

<style scoped>
.log {
  border-top: 1px solid var(--c-rule-strong);
}

.year {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem 2rem;
  padding-block: clamp(1.6rem, 4vw, 2.4rem);
  border-bottom: 1px solid var(--c-rule);
}
@media (min-width: 820px) {
  .year {
    grid-template-columns: 7rem minmax(0, 1fr);
  }
}

.year-mark {
  margin: 0;
  color: var(--c-ink-3);
  font-variant-numeric: tabular-nums;
}
@media (min-width: 820px) {
  .year-mark {
    position: sticky;
    top: 5.5rem;
    align-self: start;
  }
}

.entries {
  display: grid;
  gap: 2.2rem;
  min-width: 0;
}

.entry-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1.2rem;
  flex-wrap: wrap;
  margin-bottom: 0.9rem;
}
.entry-title {
  margin: 0 0 0.25rem;
  font-size: var(--step-2);
  font-weight: 500;
  letter-spacing: -0.015em;
  color: var(--c-ink);
  display: flex;
  align-items: center;
  gap: 0.55rem;
}
.ongoing {
  display: inline-flex;
  align-items: center;
}
.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--c-ok);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--c-ok) 22%, transparent);
}
.entry-org {
  margin: 0;
  color: var(--c-accent);
}
.entry-when {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  text-align: right;
  flex: none;
}
.entry-when time {
  color: var(--c-ink-3);
}

.bullets {
  list-style: none;
  margin: 0 0 1rem;
  padding: 0;
}
.bullets li {
  position: relative;
  padding-left: 1.1rem;
  padding-block: 0.15rem;
  color: var(--c-ink-2);
  font-size: 0.94rem;
}
.bullets li::before {
  content: '—';
  position: absolute;
  left: 0;
  color: var(--c-ink-3);
}

.techs {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin: 0;
  padding: 0;
}
.techs li {
  border: 1px solid var(--c-rule);
  border-radius: 3px;
  padding: 0.1rem 0.45rem;
  font-size: 0.68rem;
  color: var(--c-ink-2);
}
</style>
